import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, CalendarDays, Users, ClipboardList,
  Receipt, FolderOpen, UserCog, Settings, LogOut, Menu, X, ChevronDown, FileCheck,
  Contact, CreditCard, BarChart3, Package, TrendingDown, HelpCircle, Megaphone
} from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-notes-preview/artifacts/qfix72o2_Lumero_Logo_transparent_with_name-removebg-preview.png";

const NAV_ITEMS = [
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/app/patients", icon: Users, label: "Patients" },
  { to: "/app/appointments", icon: ClipboardList, label: "Appointments" },
  { to: "/app/invoices", icon: Receipt, label: "Invoices" },
  { to: "/app/payments", icon: CreditCard, label: "Payments" },
  { to: "/app/products", icon: Package, label: "Products" },
  { to: "/app/expenses", icon: TrendingDown, label: "Expenses" },
  { to: "/app/forms", icon: FileCheck, label: "Forms" },
  { to: "/app/contacts", icon: Contact, label: "Contacts" },
  { to: "/app/marketing", icon: Megaphone, label: "Marketing" },
  { to: "/app/reports", icon: BarChart3, label: "Reports" },
  { to: "/app/team", icon: UserCog, label: "Team" },
  { to: "/app/settings", icon: Settings, label: "Settings" },
  { to: "/app/support", icon: HelpCircle, label: "Help" },
];

export default function AppLayout() {
  const { user, clinic, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();

  const initials = user?.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  const roleBadge = { owner: "Owner", practitioner: "Practitioner", receptionist: "Receptionist" }[user?.role] || user?.role;

  return (
    <div className="flex h-screen bg-[#FAF9F6]" data-testid="app-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-[240px] bg-[#131E1E] text-white flex-shrink-0" data-testid="sidebar">
        <div className="p-5 border-b border-white/[0.06]">
          <img src={LOGO_URL} alt="LumeroFlow" className="h-7 w-auto brightness-0 invert opacity-90" />
        </div>
        <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.filter(i => !i.roles || i.roles.includes(user?.role)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80 hover:bg-white/[0.04]"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#205B5A] flex items-center justify-center text-xs font-semibold">{initials}</div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-white/80 truncate">{user?.full_name}</p>
              <p className="text-[10px] text-white/40 capitalize">{roleBadge}</p>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="sidebar-logout-btn"
            className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors w-full"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-[260px] bg-[#131E1E] text-white flex flex-col z-10">
            <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
              <img src={LOGO_URL} alt="LumeroFlow" className="h-7 w-auto brightness-0 invert opacity-90" />
              <button onClick={() => setSidebarOpen(false)} className="text-white/50"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 py-3 px-3 space-y-0.5 overflow-y-auto">
              {NAV_ITEMS.filter(i => !i.roles || i.roles.includes(user?.role)).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"
                    }`
                  }
                >
                  <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-[#1A1B1A]/[0.06] flex items-center justify-between px-4 md:px-6 flex-shrink-0" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-1.5"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-sidebar-toggle"
            >
              <Menu className="w-5 h-5 text-[#4A4B4A]" />
            </button>
            {clinic?.logo_url ? (
              <div className="hidden md:flex items-center gap-2.5">
                <div className="h-9 px-1.5 flex items-center justify-center bg-[#FAF9F6] rounded-lg border border-[#1A1B1A]/[0.04]">
                  <img src={clinic.logo_url} alt={clinic.name} className="h-7 w-auto max-w-[140px] object-contain" data-testid="clinic-logo-header" />
                </div>
                <span className="text-sm font-medium text-[#1A1B1A]">{clinic.name}</span>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2.5">
                {clinic?.name && <div className="w-9 h-9 rounded-lg bg-[#205B5A]/10 flex items-center justify-center text-[10px] font-bold text-[#205B5A]">{clinic.name.split(" ").map(w => w[0]).join("").slice(0,2)}</div>}
                <span className="text-sm font-medium text-[#1A1B1A]">{clinic?.name}</span>
              </div>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 hover:bg-[#FAF9F6] px-2 py-1.5 rounded-lg transition-colors"
              data-testid="profile-menu-btn"
            >
              <div className="w-7 h-7 rounded-full bg-[#205B5A]/10 flex items-center justify-center">
                <span className="text-[10px] font-semibold text-[#205B5A]">{initials}</span>
              </div>
              <span className="text-sm font-medium text-[#1A1B1A] hidden md:block">{user?.full_name}</span>
              <ChevronDown className="w-3.5 h-3.5 text-[#757675]" />
            </button>
            {profileOpen && (
              <div className="absolute right-0 mt-1 w-48 bg-white border border-[#1A1B1A]/[0.08] rounded-xl shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-[#1A1B1A]/[0.06]">
                  <p className="text-xs font-medium text-[#1A1B1A]">{user?.full_name}</p>
                  <p className="text-[10px] text-[#757675]">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setProfileOpen(false); navigate("/app/settings"); }}
                  className="w-full text-left px-3 py-2 text-sm text-[#4A4B4A] hover:bg-[#FAF9F6] transition-colors"
                  data-testid="profile-settings-btn"
                >
                  Settings
                </button>
                <button
                  onClick={() => { setProfileOpen(false); logout(); }}
                  className="w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                  data-testid="profile-logout-btn"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
